import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Instagram,
  Linkedin,
  Mail,
  MessageSquare,
  MapPin,
  ArrowUpRight,
  Apple,
  Play,
} from "lucide-react";
import { LOGO_SRC, TYPEFORM, withUTM } from "../data/landing";
import { openKaiChat } from "../lib/kaiChat";
import RequirementsModal from "./RequirementsModal";

const KAI_AVATAR = "/assets/kai.png";

const HELP = "https://help.egeez.com/en";

const KNOWLEDGE_LINKS = [
  { label: "About eGeez", to: "/about-egeez", internal: true },
  { label: "Research", to: "/research", internal: true },
  { label: "Notes", to: "/notes", internal: true },
  { label: "eGeez Academy", href: "https://academy.egeez.com/" },
  { label: "Friends Happiness Center", href: HELP },
  { label: "Import Catalogue", href: "https://help.egeez.com/en/articles/6-q1-how-do-i-upload-a-new-product" },
  { label: "Sign-up For Fans", href: `${HELP}/collections/11-sign-up-for-fans` },
  { label: "Blue Tick Promise", href: `${HELP}/collections/8-blue-tick-promise` },
  { label: "GEMS Loyalty", href: `${HELP}/collections/13-gems-for-fans` },
  { label: "Returns", href: `${HELP}/collections/5-returns` },
];

const MINOR_LINKS = [
  { label: "Requirements for Licensees", action: "openRequirementsModal" },
  { label: "Privacy Policy", to: "/privacy-policy", internal: true },
  { label: "Terms of Use", to: "/terms-of-use", internal: true },
  { label: "Refund Policy", to: "/refund-policy", internal: true },
];

const SOCIAL = [
  { label: "Instagram", href: "https://www.instagram.com/egeezapp/", Icon: Instagram },
  { label: "X", href: "https://x.com/Egeezapp", Icon: XIcon },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/egeezinc", Icon: Linkedin },
];

const DISCORD_URL = "https://discord.gg/YKJrurvZwd";

function XIcon({ size = 16, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1200 1227"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.152 327.181 476.152H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894L144.011 79.694h162.604l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z" />
    </svg>
  );
}

function DiscordIcon({ size = 18, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

export default function Footer() {
  const { pathname } = useLocation();
  const isHome = pathname === "/" || pathname === "/for-fans";
  const utmPage = pathname === "/" ? "for-fans" : pathname.replace(/^\//, "") || "for-fans";
  const T = Object.fromEntries(Object.entries(TYPEFORM).map(([k, v]) => [k, withUTM(v, utmPage)]));

  const [requirementsOpen, setRequirementsOpen] = useState(false);

  const handleLogoClick = (e) => {
    if (isHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer
      className="bg-white border-t border-black/8 pt-16 md:pt-24 pb-10"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-12">
        {/* Top row: logo + social */}
        <div className="flex flex-wrap items-center justify-between gap-6 pb-12 border-b border-black/10">
          <Link
            to="/"
            onClick={handleLogoClick}
            data-testid="footer-logo-link"
            aria-label="eGeez — back to top"
            className="inline-flex items-center"
          >
            <img
              src={LOGO_SRC}
              alt="eGeez"
              className="h-12 md:h-14 w-auto"
              draggable={false}
            />
          </Link>
          <div className="flex items-center gap-3">
            {SOCIAL.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="w-11 h-11 rounded-full border border-black/15 text-black/70 flex items-center justify-center hover:bg-[#0041E6] hover:text-white hover:border-[#0041E6] transition-colors"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Main grid: Kai card | Knowledge | Discord card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-12">
          {/* KAI CARD — most dominant */}
          <div
            className="lg:col-span-5 rounded-[28px] p-8 md:p-10 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0041E6 0%, #0033B8 60%, #001F7A 100%)",
            }}
            data-testid="footer-kai-card"
          >
            <div className="absolute bottom-3 -right-2 sm:bottom-4 sm:right-6 w-[120px] sm:w-[170px] md:w-[230px] pointer-events-none select-none">
              <img src={KAI_AVATAR} alt="" className="w-full h-auto drop-shadow-2xl" />
            </div>
            <div className="relative z-10 max-w-[64%] sm:max-w-[60%]">
              <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#FFD400] mb-4">
                Got a question?
              </div>
              <h3
                className="font-display text-white leading-[0.95]"
                style={{ fontSize: "clamp(28px, 4vw, 56px)" }}
              >
                Chat with<br />
                <span className="italic">Kai</span>
              </h3>
              <p className="mt-3 sm:mt-4 text-white/85 text-[13px] sm:text-[14px] md:text-[15px] leading-relaxed">
                Your AI helper, ready 24/7 to answer anything about eGeez.
              </p>
              <button
                type="button"
                onClick={openKaiChat}
                data-testid="footer-chat-with-kai-btn"
                className="group mt-6 inline-flex items-center gap-3 bg-white text-[#0041E6] font-semibold text-[16px] rounded-full pl-6 pr-2 h-[52px] hover:bg-[#FFD400] transition shadow-lg"
              >
                Chat with Kai
                <span className="w-[40px] h-[40px] rounded-full bg-[#0041E6] text-white flex items-center justify-center transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={18} />
                </span>
              </button>
            </div>
          </div>

          {/* KNOWLEDGE CENTER */}
          <div className="lg:col-span-3 flex flex-col items-center text-center" data-testid="footer-knowledge">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-black/45 mb-5">
              Knowledge Center
            </div>
            <ul className="space-y-3.5">
              {KNOWLEDGE_LINKS.map((l) =>
                l.internal ? (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-[15px] text-black/80 hover:text-[#0041E6] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ) : (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[15px] text-black/80 hover:text-[#0041E6] transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>

          {/* DISCORD CARD */}
          <div
            className="lg:col-span-4 rounded-[28px] p-7 md:p-8 relative overflow-hidden text-white"
            style={{ background: "#5865F2" }}
            data-testid="footer-discord-card"
          >
            <DiscordIcon
              size={120}
              className="absolute -bottom-4 -right-4 text-white/15"
            />
            <div className="relative z-10">
              <DiscordIcon size={28} className="text-white" />
              <h3
                className="mt-4 font-display leading-[0.95]"
                style={{ fontSize: "clamp(28px, 2.6vw, 38px)" }}
              >
                Why join the<br />eGeez Discord?
              </h3>
              <p className="mt-4 text-white/90 text-[14px] md:text-[15px] leading-relaxed">
                Because fandom deserves its own home. For years, fans have been scattered across platforms with no real place to connect. <strong>You are invited!</strong> No trolls.
              </p>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-discord-join-btn"
                className="mt-6 inline-flex items-center gap-2 bg-white text-[#5865F2] font-semibold text-[15px] rounded-full px-6 py-3 hover:bg-[#FFD400] hover:text-black transition shadow"
              >
                Join the server
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Secondary row: Install app | Reach Us | Minor links */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-14 pt-12 border-t border-black/10">
          {/* Install the App — coming soon */}
          <div className="md:col-span-5" data-testid="footer-install-app">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-4">
              Install the App · Coming Soon
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled
                aria-label="Download on App Store — coming soon"
                className="inline-flex items-center gap-3 bg-black text-white rounded-2xl px-5 py-3 opacity-90 cursor-not-allowed"
              >
                <Apple size={26} className="-mt-0.5" fill="currentColor" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wider opacity-80">
                    Coming soon to
                  </div>
                  <div className="text-[16px] font-semibold">App Store</div>
                </div>
              </button>
              <button
                type="button"
                disabled
                aria-label="Get it on Google Play — coming soon"
                className="inline-flex items-center gap-3 bg-black text-white rounded-2xl px-5 py-3 opacity-90 cursor-not-allowed"
              >
                <Play size={24} fill="currentColor" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wider opacity-80">
                    Coming soon to
                  </div>
                  <div className="text-[16px] font-semibold">Google Play</div>
                </div>
              </button>
            </div>
          </div>

          {/* Reach Us */}
          <div className="md:col-span-3" data-testid="footer-contact">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-4">
              Email Us
            </div>
            <a
              href="mailto:support@egeez.com"
              className="group inline-flex items-center gap-2 text-[15px] text-black hover:text-[#0041E6] transition-colors"
            >
              <Mail size={16} />
              support@egeez.com
            </a>
            <a
              href={T.tellMeAnything}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tell-us-anything"
              className="mt-3 group inline-flex items-center gap-2 text-[15px] text-black hover:text-[#0041E6] transition-colors"
            >
              <MessageSquare size={16} />
              Tell Us Anything
              <ArrowUpRight size={13} className="opacity-60 group-hover:opacity-100 transition" />
            </a>
            <div className="mt-5 flex items-start gap-2 text-[13px] text-black/60 leading-snug">
              <MapPin size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                150 King St. Toronto, ON,<br />Canada, M5H 1J9
              </span>
            </div>
          </div>

          {/* Minor links */}
          <div className="md:col-span-4" data-testid="footer-minor-links">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-4">
              Policies
            </div>
            <ul className="space-y-2.5">
              {MINOR_LINKS.map((l) => {
                if (l.action === "openRequirementsModal") {
                  return (
                    <li key={l.label}>
                      <button
                        type="button"
                        data-testid="footer-requirements-trigger"
                        onClick={() => setRequirementsOpen(true)}
                        className="text-[14px] text-black/70 hover:text-[#0041E6] transition-colors text-left"
                      >
                        {l.label}
                      </button>
                    </li>
                  );
                }
                if (l.internal) {
                  return (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        data-testid={`footer-${l.to.replace(/^\//, "")}-link`}
                        className="text-[14px] text-black/70 hover:text-[#0041E6] transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[14px] text-black/70 hover:text-[#0041E6] transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Waitlist CTA row */}
        <div className="mt-14 pt-12 border-t border-black/10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <button
              type="button"
              onClick={() => {
                const el = document.querySelector(
                  '[data-testid="premiere-drop-licensees"], [data-testid="premiere-drop-fans"]',
                );
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                } else if (pathname !== "/" && pathname !== "/for-fans" && pathname !== "/for-licensees") {
                  // navigate to fans page anchor
                  window.location.href = "/for-fans#premiere-drop-fans";
                }
              }}
              data-testid="footer-premiere-logo-btn"
              aria-label="Scroll to Premiere Drop"
              className="inline-block transition-transform hover:scale-[1.04] cursor-pointer"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/gs9xwyp4_Premimere%20Drop%20logo.png"
                alt="Premiere Drop"
                className="h-12 md:h-14 w-auto mb-3 mx-auto md:mx-0"
                draggable={false}
              />
            </button>
            <h3
              className="font-display text-[#0041E6] leading-[0.95]"
              style={{ fontSize: "clamp(24px, 2.8vw, 36px)" }}
            >
              Be the first to know.
            </h3>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2">
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-black/40">
              For Fans
            </span>
            <a
              href={T.joinWaitlist}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-join-waitlist-btn"
              className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-semibold text-[17px] rounded-full pl-7 pr-2 h-[56px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.55)]"
            >
              Join the Waitlist
              <span className="w-[42px] h-[42px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-14 pt-6 border-t border-black/10 text-center text-[12px] text-black/50">
          © 2026 All rights reserved, eGeez.
        </div>
      </div>
      <RequirementsModal open={requirementsOpen} onClose={() => setRequirementsOpen(false)} />
    </footer>
  );
}
